import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { gameAPI } from '../services/gameAPI';
import { featuredSessionId } from '../utils/featuredSession';
import { pickFeaturedRoute } from '../utils/gameView';
import { DEFAULT_THEME, themeConfig } from '../constants/themes';
import { Button } from '../components/ui/button';

const messageFor = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.public_message
    || detail?.message
    || error?.message
    || 'Unable to open this featured case.';
};

/**
 * `/featured/:slug` — the entry point into the real game screen.
 *
 * A featured case has no generation step: the compat `load` route starts the
 * case on first entry and returns exactly the payload `loadSavedGame` takes,
 * so this page hydrates the pro-gen store and hands off to the game screen:
 * `/game3d` when the case has 3D scenes, the device can show them and the
 * player has not chosen 2D, otherwise `/game` (see `pickFeaturedRoute`). It
 * renders no gameplay of its own.
 */
const FeaturedCaseEntry = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const loadSavedGame = useGameStore((state) => state.loadSavedGame);
  const storedSessionId = useGameStore((state) => state.sessionId);
  const storedTheme = useGameStore((state) => state.selectedTheme);
  // The theme travels with the compat `load` payload, so it is only known up
  // front when the store already holds THIS featured case (re-entry, or a
  // retry after a failed load). Otherwise use the app's default surface —
  // same markup as GamePage's restore screen, never a bespoke flat colour.
  const knownTheme = slug && storedSessionId === featuredSessionId(slug) ? storedTheme : null;
  const theme = themeConfig[knownTheme] || themeConfig[DEFAULT_THEME];
  const [error, setError] = useState(null);
  // The load is tied to the slug that asked for it and to a request sequence:
  //  - a second effect run for the SAME slug (StrictMode double-mount) joins
  //    the in-flight load instead of starting the case twice;
  //  - switching to another slug aborts the first request and starts a new
  //    one, and a late answer from the abandoned slug is dropped rather than
  //    hydrating the wrong case.
  const latestRef = useRef({ slug: null, seq: 0 });
  const inFlightRef = useRef(null);

  const open = useCallback(async () => {
    if (!slug) {
      setError('Featured case slug is missing.');
      return;
    }

    const active = inFlightRef.current;
    if (active && active.slug === slug) return;
    if (active && active.slug !== slug) active.controller?.abort();

    const seq = latestRef.current.seq + 1;
    latestRef.current = { slug, seq };
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    inFlightRef.current = { slug, seq, controller };
    const isCurrent = () => latestRef.current.slug === slug && latestRef.current.seq === seq;
    setError(null);

    try {
      const savedData = await gameAPI.loadGameSession(
        featuredSessionId(slug),
        controller ? { signal: controller.signal } : {},
      );
      // A superseded slug never applies: the player is already on another
      // case, and hydrating this payload would swap it out underneath them.
      if (!isCurrent()) return;
      loadSavedGame({
        theme: savedData.theme,
        difficulty: savedData.difficulty,
        caseId: savedData.caseId,
        sessionId: savedData.sessionId,
        gameState: savedData.gameState,
        messageHistory: savedData.messageHistory,
        guideCharacter: savedData.guideCharacter,
        caseData: savedData.caseData,
        lastImage: savedData.lastImage,
        lastImageVantage2: savedData.lastImageVantage2,
      });
      navigate(pickFeaturedRoute(savedData.gameState?.locations), { replace: true });
    } catch (loadError) {
      // A superseded slug never reports: its error belongs to a case the
      // player has already navigated away from.
      if (!isCurrent()) return;
      if (loadError?.name === 'CanceledError' || loadError?.name === 'AbortError'
        || loadError?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to open featured case:', loadError);
      setError(messageFor(loadError));
    } finally {
      if (inFlightRef.current?.seq === seq) inFlightRef.current = null;
    }
  }, [loadSavedGame, navigate, slug]);

  useEffect(() => {
    void open();
  }, [open]);

  if (error) {
    return (
      <main className={`min-h-screen bg-gradient-to-br ${theme.bg} text-white grid place-items-center p-6`}>
        <section role="alert" className="max-w-xl text-center space-y-4">
          <h1 className="text-2xl font-bold">Featured case unavailable</h1>
          <p className="text-red-200">{error}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => open()}>Try again</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Main menu</Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <div
      role="status"
      data-testid="featured-entry-loading"
      className={`min-h-screen bg-gradient-to-br ${theme.bg} text-white flex flex-col items-center justify-center gap-4`}
    >
      <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white/80 animate-spin" />
      <p className="text-white/70 text-sm tracking-wide">Opening your featured case...</p>
    </div>
  );
};

export default FeaturedCaseEntry;
